import {
  createStyles,
  makeStyles,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  TableBody,
  CircularProgress,
  Button,
  Theme,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React, { useState } from 'react';
import { DisputeConnection, DisputeOrderField, Dispute, DisputeStatus, OrderDirection, Maybe } from '../gql-types.generated';
import InfiniteScroller from 'react-infinite-scroller';
import { disputeStatusDisplay, disputeReasonDisplay } from '../features/challenge-dispute/ChallengeDispute';
import DisputesHeader, { HeaderCell } from './DisputesHeader';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      minWidth: 650,
      overflow: 'auto',
      tableLayout: 'fixed',
    },
    tableWrap: {
      height: 'calc(100vh - 210px)',
      minHeight: 'calc(100vh - 210px)', // or 920 to align with the 3rd box
      '&::before': {
        content: '""',
        display: 'block',
        position: 'sticky',
        height: '6px',
        top: '0.5px',
        backgroundColor: '#F5F5F5',
        boxShadow: '0px 50px 6px #797C84',
      },
    },
    actionsCell: {
      minWidth: 150,
    },
    disputed: {
      color: theme.palette.error.main,
      fontWeight: 500,
      fontSize: 16,
    },
    actionButton: {
      backgroundColor: 'white',
    },
    hidden: {
      display: 'none',
    },
    tableRow: {
      '&$selected, &$selected:hover': {
        backgroundColor: '#EEF4FE',
      },
    },
    selected: {},
    progressBox: {
      height: 60,
      width: 60,
      display: 'inline-block',
    },
    tablecell: {
      fontSize: 16,
      wordWrap: 'break-word',
    },
  }),
);

// Table for the payment requests
interface DisputeListProps {
  defaultCurrency?: string | null;
  disputeConnection: DisputeConnection;
  disputes: Dispute[] | undefined;
  direction: OrderDirection;
  field: string;
  handleSort: (arrowDirection: OrderDirection, fieldName: string) => void;
  loadPage: (endEdge: string) => void;
  selectedRecord?: Dispute;
  setSelectedRecord: (record: Dispute | undefined) => void;
  handleChargebackReview: (record: Dispute) => void;
  handleViewDispute: (record: Dispute) => void;
  isLoadingDisputes: boolean;
}

const disputeHeaderCells: HeaderCell[] = [
  { id: 'status', sortable: false, label: 'Status' },
  { id: 'reason', sortable: false, label: 'Reason' },
  {
    id: 'dateSettled',
    sortable: true,
    label: 'Date Settled',
    direction: OrderDirection.Desc,
    field: DisputeOrderField.Timestamp,
  },
  { id: 'amount', sortable: false, label: 'Amount', align: 'right' },
  { id: 'actions', sortable: false, label: '', align: 'center' },
];

const DisputeList: React.FC<DisputeListProps> = props => {
  const classes = useStyles();
  const {
    defaultCurrency,
    disputeConnection,
    disputes,
    direction,
    field,
    handleSort,
    handleChargebackReview,
    handleViewDispute,
    loadPage,
    selectedRecord,
    setSelectedRecord,
    isLoadingDisputes,
  } = props;

  // Row hover state.
  const [hoveredId, setHoveredId] = useState(''); //If a column is being hovered over

  // Dispute object fields
  const hasMoreData = disputeConnection?.pageInfo.hasNextPage as boolean;

  // Handling row selection
  const handleRowClick = (event: unknown, row: Dispute) => {
    if (row) {
      // Select / unselect if already selected
      setSelectedRecord(row.id === selectedRecord?.id ? undefined : row);
    }
  };

  // Handling a row hover. Hovering over a row reveals the button that will trigger the detail modal. Modal is still TODO
  const handleHover = (event: React.MouseEvent<unknown>, id: string) => {
    setHoveredId(id);
  };

  const handleMouseLeave = () => {
    setHoveredId('');
  };

  const handlePageLoad = (page: number) => {
    console.log(`loading page: ${page}`);
    if (!disputeConnection) {
      // There is not connection to use for retrieving the next page
      return;
    }

    // No more pages to load, no need to send the load request
    if (!disputeConnection.pageInfo.hasNextPage) {
      return;
    }

    if (!disputeConnection.pageInfo.endCursor) {
      return;
    }

    loadPage(disputeConnection.pageInfo.endCursor);
  };

  const calculateDaysLeft = (createdAt: string) => {
    const dueDate = new Date(createdAt);
    const today = new Date();
    const difference = today.valueOf() - dueDate.valueOf();
    if (difference < 0) {
      return '0 days';
    }
    const days = Math.floor(difference / 86400000);
    if (days > 7) {
      return '0 days';
    }
    const remaining = 7 - days;
    return remaining === 1 ? '1 day' : `${remaining} days`;
  };

  const skeletonDesktop: JSX.Element[] = [];
  for (let i = 0; i < 10; i += 1) {
    skeletonDesktop.push(
      <TableRow key={i}>
        <TableCell style={{ paddingLeft: '60px' }}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell align="right">
          <Skeleton height="1.5em" width="60%" style={{ marginLeft: 'auto' }} />
        </TableCell>
        <TableCell />
      </TableRow>,
    );
  }

  return (
    <TableContainer className={classes.tableWrap}>
      <InfiniteScroller
        pageStart={0}
        loadMore={handlePageLoad}
        hasMore={hasMoreData}
        loader={
          <div className={classes.progressBox}>
            <CircularProgress key={0} size={42} />
          </div>
        }
        initialLoad={false}
        useWindow={false}
      >
        <Table className={classes.table} aria-label="Disputes Table" stickyHeader>
          <col width="25%" />
          <col width="25%" />
          <col width="15%" />
          <col width="15%" />
          <col width="20%" />
          <DisputesHeader order={direction} orderBy={field} handleSort={handleSort} cells={disputeHeaderCells} />
          <TableBody data-cy="dispute-table-body">
            {isLoadingDisputes ? (
              <>{skeletonDesktop}</>
            ) : (
              disputes?.map((row: Maybe<Dispute>, index) => {
                const node = row;
                if (!node) {
                  return (
                    <TableRow>
                      <TableCell colSpan={disputeHeaderCells.length}></TableCell>
                    </TableRow>
                  );
                }
                const isSelected = node.id === selectedRecord?.id;
                const isHovered = node.id === hoveredId;
                const alertMerchant = node.status === DisputeStatus.AwaitingMerchantResponse;
                const settledDate = node.resolvedAt ? new Date(node.resolvedAt).toLocaleDateString() : 'N/A';
                return (
                  <TableRow
                    className={classes.tableRow}
                    classes={{ selected: classes.selected }}
                    key={typeof node.id === 'string' ? node.id : index}
                    hover
                    selected={isSelected}
                    onClick={event => {
                      handleRowClick(event, node);
                    }}
                    onMouseEnter={event => {
                      if (node.id) {
                        handleHover(event, node.id);
                      }
                    }}
                    onMouseLeave={handleMouseLeave}
                    onDoubleClick={() => (alertMerchant ? handleChargebackReview(node) : handleViewDispute(node))}
                  >
                    <TableCell className={alertMerchant ? classes.disputed : classes.tablecell} style={{ paddingLeft: '60px' }}>
                      {
                        disputeStatusDisplay.find(item => {
                          return item.name === node.status;
                        })?.display
                      }
                    </TableCell>
                    <TableCell className={alertMerchant ? classes.disputed : classes.tablecell}>
                      {
                        disputeReasonDisplay.find(item => {
                          return item.name === node.reason;
                        })?.display
                      }
                    </TableCell>
                    <TableCell className={alertMerchant ? classes.disputed : classes.tablecell}>
                      {alertMerchant ? `${calculateDaysLeft(node.createdAt)} left` : settledDate}
                    </TableCell>
                    <TableCell className={alertMerchant ? classes.disputed : classes.tablecell} align="right">
                      {/* TODO: Shouldn't there be a currency on the dispute? */}
                      {typeof node.amount === 'number'
                        ? new Intl.NumberFormat('en', {
                            style: 'currency',
                            currency: defaultCurrency || 'USD',
                            currencyDisplay: 'symbol',
                          }).format(node.amount / 100)
                        : null}
                    </TableCell>
                    <TableCell align="center" className={classes.actionsCell} padding="checkbox">
                      <Button
                        variant="outlined"
                        color="primary"
                        aria-label={alertMerchant ? 'Review' : 'View details'}
                        className={alertMerchant || isHovered ? classes.actionButton : classes.hidden}
                        onClick={() => (alertMerchant ? handleChargebackReview(node) : handleViewDispute(node))}
                        data-cy="view-dispute"
                      >
                        {alertMerchant ? 'Review' : 'Details'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </InfiniteScroller>
    </TableContainer>
  );
};

export default DisputeList;
